import type React from 'react';
import { Link } from 'react-router-dom';
import Select from '../../../components/form/AppSelect';
import NumberInput from '../../../components/form/NumberInput';
import { useTranslation } from 'react-i18next';
import EmptyState from '../../../components/feedback/EmptyState';
import PageSection from '../../../components/layout/PageSection';
import { getContrastColor } from '../../../shared/colorUtils';
import { formatDimensions, formatWeight } from '../../../shared/units';
import type { CaseDetailData, CaseStockItemOption, CaseUnitRow } from '../types';

export interface CaseStockOption {
  value: number | string;
  label: string;
}

interface CaseDetailsTabProps {
  itemCase: CaseDetailData;
  editingQty: boolean;
  qtyTotal: number;
  qtyAvailable: number;
  setEditingQty: React.Dispatch<React.SetStateAction<boolean>>;
  setQtyTotal: React.Dispatch<React.SetStateAction<number>>;
  setQtyAvailable: React.Dispatch<React.SetStateAction<number>>;
  savingQty: boolean;
  onSaveQty: () => void;
  showLinkForm: boolean;
  stockItemOptions: CaseStockItemOption[];
  selectedStockItem: CaseStockItemOption | null;
  setShowLinkForm: React.Dispatch<React.SetStateAction<boolean>>;
  setSelectedStockItem: React.Dispatch<React.SetStateAction<CaseStockItemOption | null>>;
  onSearchStockItem: (value: string) => void;
  onSaveStockItem: (itemId: number | string | null) => void;
  savingStockItem: boolean;
  units: CaseUnitRow[];
  unitsLoading: boolean;
  deletingUnitId: number | string | null;
  onDeleteUnit: (unitId: number | string) => void;
  onPrintLabels: () => void;
  onOpenBulkModal: () => void;
  unitStatusColors: Record<string, string>;
  stockOptions: CaseStockOption[];
  canChangeUnitStock: boolean;
  onChangeUnitStock: (unitId: number | string, stockId: number | string | null) => void;
  savingUnitStockId: number | string | null;
}

const CaseDetailsTab = ({
  itemCase,
  editingQty,
  qtyTotal,
  qtyAvailable,
  setEditingQty,
  setQtyTotal,
  setQtyAvailable,
  savingQty,
  onSaveQty,
  showLinkForm,
  stockItemOptions,
  selectedStockItem,
  setShowLinkForm,
  setSelectedStockItem,
  onSearchStockItem,
  onSaveStockItem,
  savingStockItem,
  units,
  unitsLoading,
  deletingUnitId,
  onDeleteUnit,
  onPrintLabels,
  onOpenBulkModal,
  unitStatusColors,
  stockOptions,
  canChangeUnitStock,
  onChangeUnitStock,
  savingUnitStockId,
}: CaseDetailsTabProps) => {
  const { t } = useTranslation();
  const stockBreakdown = itemCase.stock_breakdown ?? [];

  return (
    <div className="d-flex flex-column gap-4">
      <PageSection
        title={t('warehouses.items.detail')}
        description={t('warehouses.cases.detailsDescription', 'Core identifiers, quantities and operational notes for this case.')}
      >
        <div className="datagrid">
          <div className="datagrid-item">
            <div className="datagrid-title">{t('warehouses.cases.name')}</div>
            <div className="datagrid-content">{itemCase.name}</div>
          </div>
          {itemCase.qr_code && (
            <div className="datagrid-item">
              <div className="datagrid-title">{t('warehouses.cases.qrCode')}</div>
              <div className="datagrid-content"><code>{itemCase.qr_code}</code></div>
            </div>
          )}
          <div className="datagrid-item">
            <div className="datagrid-title">{t('warehouses.cases.emptyWeight')}</div>
            <div className="datagrid-content">{formatWeight(itemCase.weight ?? 0, itemCase.weight_unit_symbol)}</div>
          </div>
          <div className="datagrid-item">
            <div className="datagrid-title">{t('warehouses.cases.outerDimensions')}</div>
            <div className="datagrid-content">
              {formatDimensions(
                itemCase.outer_length,
                itemCase.outer_width,
                itemCase.outer_height,
                itemCase.outer_dimension_unit_symbol,
              )}
            </div>
          </div>
          <div className="datagrid-item">
            <div className="datagrid-title">{t('warehouses.cases.innerDimensions')}</div>
            <div className="datagrid-content">
              {formatDimensions(
                itemCase.inner_length,
                itemCase.inner_width,
                itemCase.inner_height,
                itemCase.inner_dimension_unit_symbol,
              )}
            </div>
          </div>
          <div className="datagrid-item">
            <div className="datagrid-title">{t('warehouses.cases.stocks')}</div>
            <div className="datagrid-content">
              {stockBreakdown.length > 0 ? (
                <div className="d-flex flex-wrap gap-1">
                  {stockBreakdown.map((row) => (
                    <span
                      key={row.stock ?? 'none'}
                      className={`badge ${row.stock === null ? 'bg-secondary-lt' : 'bg-azure-lt'}`}
                    >
                      {row.stock === null ? t('warehouses.cases.noStock') : row.stock_name} ×{row.count}
                    </span>
                  ))}
                </div>
              ) : (
                <span className="text-muted">—</span>
              )}
            </div>
          </div>
          {!editingQty ? (
            <>
              <div className="datagrid-item">
                <div className="datagrid-title">{t('warehouses.quantityTotal')}</div>
                <div className="datagrid-content d-flex align-items-center gap-2">
                  <span className="fw-semibold">{itemCase.quantity_total}</span>
                  <button className="btn btn-sm btn-ghost-secondary py-0 px-1" onClick={() => setEditingQty(true)}>
                    <i className="ti ti-pencil" aria-hidden="true" />
                  </button>
                </div>
              </div>
              <div className="datagrid-item">
                <div className="datagrid-title">{t('warehouses.quantityAvailable')}</div>
                <div className="datagrid-content">
                  <span className={`fw-semibold ${itemCase.quantity_available === 0 ? 'text-danger' : 'text-success'}`}>
                    {itemCase.quantity_available}
                  </span>
                </div>
              </div>
            </>
          ) : (
            <div className="datagrid-item" style={{ gridColumn: 'span 2' }}>
              <div className="datagrid-title">{t('warehouses.quantityTotal')} / {t('warehouses.quantityAvailable')}</div>
              <div className="datagrid-content d-flex align-items-center gap-2 flex-wrap">
                <div className="d-flex align-items-center gap-1">
                  <span className="text-muted small">{t('warehouses.quantityTotal')}:</span>
                  <NumberInput
                    min={0}
                    step="any"
                    size="sm"
                    style={{ width: 80 }}
                    value={qtyTotal}
                    onChange={setQtyTotal}
                  />
                </div>
                <div className="d-flex align-items-center gap-1">
                  <span className="text-muted small">{t('warehouses.quantityAvailable')}:</span>
                  <NumberInput
                    min={0}
                    step="any"
                    size="sm"
                    style={{ width: 80 }}
                    value={qtyAvailable}
                    onChange={setQtyAvailable}
                  />
                </div>
                <button className="btn btn-sm btn-primary" disabled={savingQty} onClick={onSaveQty}>
                  {savingQty ? '…' : t('common.save')}
                </button>
                <button
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => {
                    setEditingQty(false);
                    setQtyTotal(itemCase.quantity_total ?? 0);
                    setQtyAvailable(itemCase.quantity_available ?? 0);
                  }}
                >
                  {t('common.cancel')}
                </button>
              </div>
            </div>
          )}
          {itemCase.description && (
            <div className="datagrid-item">
              <div className="datagrid-title">{t('warehouses.bundles.description')}</div>
              <div className="datagrid-content">{itemCase.description}</div>
            </div>
          )}
          <div className="datagrid-item">
            <div className="datagrid-title">{t('warehouses.bundles.itemsPerCase')}</div>
            <div className="datagrid-content fw-semibold">{itemCase.items_per_case}</div>
          </div>
          {itemCase.notes && (
            <div className="datagrid-item">
              <div className="datagrid-title">{t('warehouses.bundles.notes')}</div>
              <div className="datagrid-content text-muted">{itemCase.notes}</div>
            </div>
          )}
        </div>
      </PageSection>

      <PageSection
        title={t('warehouses.bundles.stockItem')}
        description={t('warehouses.cases.stockItemDescription', 'Link the case to a stock item so physical availability stays aligned across flows.')}
        actions={
          <button
            className="btn btn-sm btn-outline-secondary"
            onClick={() => {
              setShowLinkForm((value) => !value);
              setSelectedStockItem(null);
            }}
          >
            {itemCase.stock_item ? t('warehouses.bundles.changeStockItem') : t('warehouses.bundles.linkStockItem')}
          </button>
        }
      >
        {itemCase.stock_item ? (
          <div className="d-flex align-items-center gap-4 flex-wrap">
            <div>
              <div className="text-muted small">{t('warehouses.bundles.stockItemName')}</div>
              <div className="fw-semibold">{itemCase.stock_item_name}</div>
              {itemCase.stock_item_stock_name && (
                <div className="text-muted small">{itemCase.stock_item_stock_name}</div>
              )}
            </div>
            <div>
              <div className="text-muted small">{t('warehouses.quantityTotal')}</div>
              <div className="fw-semibold fs-4">{itemCase.stock_item_quantity_total ?? '—'}</div>
            </div>
            <div>
              <div className="text-muted small">{t('warehouses.quantityAvailable')}</div>
              <div className={`fw-semibold fs-4 ${(itemCase.stock_item_quantity_available ?? 0) === 0 ? 'text-danger' : 'text-success'}`}>
                {itemCase.stock_item_quantity_available ?? '—'}
              </div>
            </div>
            <div className="ms-auto">
              <button className="btn btn-sm btn-outline-danger" disabled={savingStockItem} onClick={() => onSaveStockItem(null)}>
                {t('warehouses.bundles.unlinkStockItem')}
              </button>
            </div>
          </div>
        ) : (
          <EmptyState
            className="py-4"
            icon={<i className="ti ti-link-off app-empty-state__icon-glyph" aria-hidden="true" />}
            title={t('warehouses.bundles.noStockItem')}
            message={t('warehouses.cases.noStockItemMessage', 'This case is not linked to any stock item yet.')}
          />
        )}
        {showLinkForm && (
          <div className="mt-3 d-flex gap-2 align-items-end flex-wrap" style={{ maxWidth: 560 }}>
            <div className="flex-grow-1" style={{ minWidth: 280 }}>
              <label className="form-label">{t('warehouses.bundles.searchItem')}</label>
              <Select
                options={stockItemOptions}
                value={selectedStockItem}
                onChange={(option) => setSelectedStockItem(option as CaseStockItemOption | null)}
                onInputChange={onSearchStockItem}
                isClearable
                placeholder={t('warehouses.bundles.searchItem')}
                filterOption={() => true}
                classNamePrefix="react-select"
              />
            </div>
            <button
              className="btn btn-primary"
              disabled={!selectedStockItem || savingStockItem}
              onClick={() => selectedStockItem && onSaveStockItem(selectedStockItem.value)}
            >
              {savingStockItem ? '…' : t('common.save')}
            </button>
            <button
              className="btn btn-outline-secondary"
              onClick={() => {
                setShowLinkForm(false);
                setSelectedStockItem(null);
              }}
            >
              {t('common.cancel')}
            </button>
          </div>
        )}
      </PageSection>

      <PageSection
        title={t('warehouses.units.title')}
        description={t('warehouses.cases.unitsDescription', 'Track physical units, print labels and remove only units that are currently available.')}
        actions={
          <div className="btn-list">
            <button className="btn btn-sm btn-outline-secondary" onClick={onPrintLabels} disabled={units.length === 0}>
              <i className="ti ti-printer me-2" aria-hidden="true" />
              {t('warehouses.units.printLabels')}
            </button>
            <button className="btn btn-sm btn-primary" onClick={onOpenBulkModal}>
              <i className="ti ti-plus me-2" aria-hidden="true" />
              {t('warehouses.units.addUnits')}
            </button>
          </div>
        }
        bodyClassName="p-0"
      >
        {unitsLoading ? (
          <div className="p-3 text-center text-muted">{t('common.loading')}</div>
        ) : units.length === 0 ? (
          <EmptyState
            className="py-5"
            icon={<i className="ti ti-package-off app-empty-state__icon-glyph" aria-hidden="true" />}
            title={t('warehouses.units.noUnits')}
            message={t('warehouses.cases.noUnitsMessage', 'Generate physical units to start label printing and assignment tracking.')}
          />
        ) : (
          <div className="table-responsive">
            <table className="table table-vcenter card-table app-table mb-0">
              <thead>
                <tr>
                  <th>{t('warehouses.units.serialNumber')}</th>
                  <th>{t('warehouses.units.qrCode')}</th>
                  <th>{t('warehouses.units.status')}</th>
                  <th>{t('warehouses.units.condition')}</th>
                  <th>{t('warehouses.cases.stock')}</th>
                  <th>{t('warehouses.units.location')}</th>
                  <th>{t('warehouses.units.assignedTo')}</th>
                  <th>{t('projects.startDate')}</th>
                  <th>{t('projects.endDate')}</th>
                  <th className="w-1"></th>
                </tr>
              </thead>
              <tbody>
                {units.map((unit) => {
                  const statusColor = unitStatusColors[unit.status] ?? 'var(--tblr-secondary)';
                  return (
                    <tr key={unit.id}>
                      <td>{unit.serial_number || <span className="text-muted">—</span>}</td>
                      <td><code className="small">{unit.qr_code}</code></td>
                      <td>
                        <span className="badge" style={{ backgroundColor: statusColor, color: getContrastColor(statusColor) }}>
                          {t(`warehouses.units.status_${unit.status}`, unit.status)}
                        </span>
                      </td>
                      <td>{unit.condition_display || unit.condition}</td>
                      <td style={{ minWidth: 180 }}>
                        {canChangeUnitStock ? (
                          <Select
                            options={stockOptions}
                            value={stockOptions.find((option) => String(option.value) === String(unit.stock)) ?? null}
                            onChange={(option) =>
                              onChangeUnitStock(unit.id, (option as CaseStockOption | null)?.value ?? null)
                            }
                            isClearable
                            isDisabled={savingUnitStockId === unit.id}
                            placeholder={t('warehouses.cases.noStock')}
                            aria-label={t('warehouses.cases.stock')}
                            menuPosition="fixed"
                            classNamePrefix="react-select"
                          />
                        ) : (
                          unit.stock_name || <span className="text-muted">—</span>
                        )}
                      </td>
                      <td>{unit.location_note || <span className="text-muted">—</span>}</td>
                      <td>
                        {unit.current_project_ref ? (
                          <Link to={`/projects/detail/${unit.current_project}`} className="fw-medium">
                            {unit.current_project_ref}
                          </Link>
                        ) : unit.current_contract_ref ? (
                          <Link to={`/contracts/contract/${unit.current_contract}`} className="fw-medium">
                            {unit.current_contract_ref}
                          </Link>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                      <td className="text-nowrap">{unit.current_project_start || <span className="text-muted">—</span>}</td>
                      <td className="text-nowrap">{unit.current_project_end || <span className="text-muted">—</span>}</td>
                      <td>
                        <button
                          className="btn btn-ghost-danger btn-sm"
                          onClick={() => onDeleteUnit(unit.id)}
                          disabled={deletingUnitId === unit.id || unit.status !== 'available'}
                          title={unit.status !== 'available' ? t('warehouses.units.cannotDeleteActive') : t('common.delete')}
                        >
                          <i className="ti ti-trash" aria-hidden="true" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </PageSection>
    </div>
  );
};

export default CaseDetailsTab;
